import { APP_NAME, ERROR_SOURCES } from '../constants/app.constants';
import { isLocalhostRuntime } from './environmentUtils';
import { deviceType, isMobileDevice, isPwaModeEnabled, safeJsonStringify } from './helpers';
import StorageHelper from './storageHelper';

const ERROR_RATE_WINDOW_MS = 60_000;
const ERROR_MAX_COUNT_PER_WINDOW = 10;
const ERROR_RATE_MAP_MAX_SIZE = 1000;

const errorRateMap = new Map();

let runtimeErrorHandlerRef = null;
let promiseRejectionHandlerRef = null;

function runtimeErrorHandler(event) {
  if (!event?.error || event?.error?.__handledByVue || event.error?.__errorMonitoringInternal)
    return;

  reportError(event.error, {
    source: ERROR_SOURCES.RUNTIME,
    file: event.filename,
    line: event.lineno,
    route: window.location.pathname,
  });
}

function promiseRejectionHandler(event) {
  try {
    const error = event.reason;
    if (!(error instanceof Error)) return;
    if (error.__errorMonitoringInternal) return;

    const isAxiosError = typeof error === 'object' && error !== null && error.isAxiosError === true;

    if (!isAxiosError) {
      reportError(error, {
        source: ERROR_SOURCES.PROMISE,
        route: window.location.pathname,
      });
    }
    // eslint-disable-next-line no-empty
  } catch (_) {}
}

export function initErrorMonitoring() {
  if (runtimeErrorHandlerRef) {
    window.removeEventListener('error', runtimeErrorHandlerRef, true);
  }
  if (promiseRejectionHandlerRef) {
    window.removeEventListener('unhandledrejection', promiseRejectionHandlerRef);
  }

  runtimeErrorHandlerRef = runtimeErrorHandler;
  promiseRejectionHandlerRef = promiseRejectionHandler;

  window.addEventListener('error', runtimeErrorHandlerRef, true);
  window.addEventListener('unhandledrejection', promiseRejectionHandlerRef);
}

export function reportError(error, context) {
  try {
    const isLocalRuntime = isLocalhostRuntime();
    const allowLocalLogging = process.env.VUE_APP_ENABLE_ERROR_LOGGING_ON_LOCAL === 'true';
    if (isLocalRuntime && !allowLocalLogging) return;

    const normalized = normalizeError(error, context);

    if (!shouldReportError(normalized)) {
      return;
    }

    sendErrorLog(normalized);
  } catch (internalError) {
    internalError.__errorMonitoringInternal = true;
  }
}

function sendErrorLog(normalizedError) {
  const baseUrl = process.env.VUE_APP_API_BASE_URL;
  if (!baseUrl) return;

  const targetUrl = baseUrl + '/api/ClientTelemetry/error';
  const clientIdStorage = new StorageHelper({
    id: process.env.VUE_APP_STORAGE_PMAKTIF_CLIENT_ID,
  });
  const clientId = clientIdStorage.get();
  const osType = deviceType();
  const addToHomeScreen = isMobileDevice() && isPwaModeEnabled();

  const payloadData = safeJsonStringify({
    ...normalizedError,
    app: APP_NAME,
    clientId,
    addToHomeScreen,
    osType,
  });
  try {
    navigator.sendBeacon(targetUrl, payloadData);
    // eslint-disable-next-line no-empty
  } catch (_) {}
}
/**
 * Reusable helper to log errors with any structured metadata
 *
 * @param {Error|string} error
 * @param {Object} metadataObj
 * @param {ErrorContext} [context={}]
 */
export function reportWithMetadata(error, metadataObj, context = {}) {
  reportError(error, {
    ...context,
    metadata: metadataObj,
  });
}
/**
 * @typedef {Object} ErrorContext
 * @property {'pre_init' | 'runtime' | 'vue' | 'promise' | 'resource' | 'unknown'} source
 * @property {string=} component // Vue component name
 * @property {string=} info // Vue error info
 * @property {string=} route
 * @property {string=} file
 * @property {number=} line
 */

/**
 * @param {unknown} error
 * @param {ErrorContext} [context={}]
 * @typedef {Object} NormalizedError
 * @property {string} message
 * @property {string|null} stack
 * @property {string} name
 * @property {string} source
 * @property {string|null} component
 * @property {string|null} route
 * @property {string|null} file
 * @property {string|null} target
 * @property {Object} metadata
 * @property {string|null} line
 * @property {number} timestamp
 */

/**
 * Converts any thrown value into a consistent error payload.
 *
 * @param {unknown} error
 * @param {ErrorContext} [context={}]
 * @returns {NormalizedError}
 */
function normalizeError(error, context = {}) {
  const isErrorObject = error instanceof Error;
  const isAxiosHttpError =
    typeof error === 'object' &&
    error !== null &&
    'config' in error &&
    typeof error.config?.url === 'string';

  if (isAxiosHttpError) error.__isAxiosHttpError = true;

  return {
    message: isErrorObject ? error?.message : typeof error === 'string' ? error : String(error),
    stack: isErrorObject ? error?.stack || null : null,
    name: error?.name || 'Error',
    source: context.source || ERROR_SOURCES.UNKNOWN,
    component: context.component || null,
    route: context.route || window.location.pathname,
    file: context.file || null,
    target: isAxiosHttpError ? error.config?.url : null,
    metadata: context.metadata || null,
    line: context.line != null ? String(context.line) : null,
    timestamp: Date.now(),
  };
}

function getErrorFingerprint(normalized) {
  return [
    normalized.source,
    normalized.name,
    normalized.message,
    normalized.component,
    normalized.file,
    normalized.line,
    normalized.target,
  ]
    .filter(Boolean)
    .join('|')
    .slice(0, 500);
}

function shouldReportError(normalizedError) {
  const now = Date.now();
  const key = getErrorFingerprint(normalizedError);

  if (errorRateMap.size > ERROR_RATE_MAP_MAX_SIZE) {
    errorRateMap.delete(errorRateMap.keys().next().value);
  }

  let entry = errorRateMap.get(key);

  if (!entry) {
    errorRateMap.set(key, { count: 1, firstSeen: now });
    return true;
  }

  // window expired
  if (now - entry.firstSeen > ERROR_RATE_WINDOW_MS) {
    entry.count = 1; // reset
    entry.firstSeen = now;
    return true;
  }

  // active window, increase count
  entry.count++;

  if (entry.count > ERROR_MAX_COUNT_PER_WINDOW) {
    return false;
  }

  return true;
}
